import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MetricsLibrary } from './MetricsLibrary';
import { getSection } from '@/config/navigation';
import { PageSection } from '@/components/ui/page-section';
import { useSettings } from '@/services/SettingsContext';
import { createCapsifiService, ValueStream as CapsifiValueStream } from '@/services/CapsifiService';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Settings2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataLoader } from '../OrbusStreamNavigatorComponent/lib/DataLoading/DataLoader';
import { MetricsService, MetricTimeseriesData } from '@/services/MetricsService';
import { convertMetricsFromResponse } from '@/services/metricsConverter';
import { Metric } from '@/types/metrics';

type AggregationLevel = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export function MetricsSection() {
  const section = getSection('metrics');
  const { apiSettings, settings, updateSettings, setShowModelSelector, isConfigured } = useSettings();
  const [error, setError] = useState<string | null>(null);
  const [valueStreams, setValueStreams] = useState<CapsifiValueStream[]>([]);
  const [selectedValueStreamId, setSelectedValueStreamId] = useState<string | null>(null);
  const [selectedValueStream, setSelectedValueStream] = useState<CapsifiValueStream | null>(null);

  // State for data loading
  const [graphData, setGraphData] = useState<any>(null);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [timeseriesData, setTimeseriesData] = useState<MetricTimeseriesData | null>(null);
  const [timeseriesLoading, setTimeseriesLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [graphDataLoading,  setGraphDataLoading] = useState(true);
  const [valueStreamsLoading, setValueStreamsLoading] = useState(true);
  const [timeseriesError, setTimeseriesError] = useState<string | null>(null);
  const [aggregation, setAggregation] = useState<AggregationLevel>('daily');
  const [showKPIMode, setShowKPIMode] = useState(false);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'threeMonths' | 'sixMonths' | 'year'>('threeMonths');

  // Check if we should fetch data
  const shouldFetchData = 
    Boolean(apiSettings.selectedModelId) && 
    Boolean(isConfigured);

  // Always initialize the service, but we'll control when it makes API calls
  const service = useMemo(() => 
    createCapsifiService(apiSettings.selectedModelId || ''), 
    [apiSettings.selectedModelId]
  );

  // Create DataLoader instance (memoized to prevent infinite loops)
  const dataLoader = useMemo(() => new DataLoader(), []);

  // Load graph data using DataLoader
  const loadGraphData = useCallback(async () => {
    if (!selectedValueStreamId) {
      console.debug('No value stream ID provided to loadGraphData');
      return null;
    }

    try {
      console.debug('Loading graph data for value stream:', selectedValueStreamId);
      const data = await dataLoader.loadDataFromApi(service, selectedValueStreamId);
      console.debug('Graph data loaded:', data);
      setGraphData(data);
      return data;
    } catch (err) {
      console.error('Error loading graph data:', err);
      setGraphData(null);
      return null;
    }
  }, [selectedValueStreamId, service]);

  // Load metrics data when graphData is available
  const loadMetricsData = useCallback(async () => {
    if (!selectedValueStreamId || !graphData) {
      console.debug('No value stream ID or graph data provided to loadMetricsData');
      return;
    }

    try {
      // Create MetricsService instance
      const metricsService = new MetricsService();
      
      // Fetch raw metrics data with value stream ID
      const rawMetricsData = await metricsService.getCapsifiVsMetricsRaw({ itemId: selectedValueStreamId });
      console.debug('Raw metrics data fetched:', rawMetricsData);
      
      // Check if capabilities exist in graph data
      if (!graphData.capabilities || !Array.isArray(graphData.capabilities)) {
        console.error('Graph data has no capabilities array:', graphData);
        return;
      }

      // Convert metrics using graphData capabilities
      const convertedMetrics = convertMetricsFromResponse(rawMetricsData, graphData.capabilities);
      
      // Update metrics state
      setMetrics(convertedMetrics);
    } catch (err) {
      console.error('Error loading metrics data:', err);
      setMetrics([]);
    }
  }, [selectedValueStreamId, graphData]);

  // Fetch timeseries data when aggregation changes
  const fetchTimeseriesData = useCallback(async () => {
    if (!showKPIMode) {
      setTimeseriesLoading(true);
      setTimeseriesError(null);
      try {
        const metricsService = new MetricsService();
        const data = await metricsService.getMetricsTimeseries(aggregation);
        setTimeseriesData(data);
        
        // Debug: check which metrics have matching keys in the API response
        if (data) {
          const availableKeys = Object.keys(data);
          
          // Check if any of our metrics have matching keys in the API response
          const metricsWithMatchingKeys = metrics.filter(m => 
            availableKeys.includes(m.metricValueKey)
          );
          
          const metricsWithoutMatchingKeys = metrics.filter(m => 
            !availableKeys.includes(m.metricValueKey)
          );
          
          if (metricsWithoutMatchingKeys.length > 0) {
            console.debug('First 5 metrics without matching keys:', metricsWithoutMatchingKeys.slice(0, 5).map(m => ({
              name: m["KPI Name"],
              key: m.metricValueKey
            })));
          }
          
          // Check for metrics with potential mapping to API keys
          const metricKeyMappings: Record<string, string[]> = {};
          metrics.forEach(metric => {
            const key = metric.metricValueKey;
            const similarKeys = availableKeys.filter(apiKey => 
              apiKey.includes(key) || key.includes(apiKey)
            );
            if (similarKeys.length > 0) {
              metricKeyMappings[key] = similarKeys;
            }
          });
        }
      } catch (error) {
        console.error('Error fetching timeseries data:', error);
        setTimeseriesError(error instanceof Error ? error.message : 'Failed to fetch metrics data');
        setTimeseriesData(null);
      } finally {
        setTimeseriesLoading(false);
      }
    }
  }, [aggregation, showKPIMode, metrics]);

  // Fetch value streams list
  const fetchValueStreams = useCallback(async () => {
    if (!shouldFetchData) {
      console.debug('Missing required configuration - cannot fetch value streams');
      return;
    }

    try {
      const data = await service.getValueStreams();
      setValueStreams(data);
      
      // If we have value streams and none is selected, select the first one
      if (data.length > 0 && !selectedValueStreamId) {
        setSelectedValueStreamId(data[0].id);
        setSelectedValueStream(data[0]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch value streams list';
      console.error('Error fetching value streams list:', errorMessage);
      
      if (errorMessage.includes('Unauthorized')) {
        setShowModelSelector(true);
      }
    }
  }, [service, shouldFetchData, selectedValueStreamId, setShowModelSelector]);

  // Fetch value streams list when dependencies change
  useEffect(() => {
    const fetchData = async () => {
      if (!shouldFetchData) return;
      
      setValueStreamsLoading(true);
      try {
        await fetchValueStreams();
      } catch (error) {
        console.error('Failed to fetch value streams:', error);
      } finally {
        setValueStreamsLoading(false);
      }
    };

    fetchData();
  }, [shouldFetchData, fetchValueStreams]);

  // Update selected value stream object when ID changes
  useEffect(() => {
    if (selectedValueStreamId && valueStreams.length > 0) {
      const selected = valueStreams.find(vs => vs.id === selectedValueStreamId) || null;
      setSelectedValueStream(selected);
      
      // Update selected capability in settings
      if (selected) {
        updateSettings({ selectedCapability: selected.label || 'all' });
      }
    } else {
      setSelectedValueStream(null);
      updateSettings({ selectedCapability: 'all' });
    }
  }, [selectedValueStreamId, valueStreams, updateSettings]);

  // Load graph data when selectedValueStreamId changes
  useEffect(() => {
    if (!shouldFetchData) return;
    const loadData = async () => {
      setGraphDataLoading(true);
      try {
        await loadGraphData();
      } finally {
        setGraphDataLoading(false);
      }
    };
    loadData();
  }, [loadGraphData, shouldFetchData]);

  // Load metrics data when graphData changes
  useEffect(() => {
    if (!shouldFetchData) return;
    const loadData = async () => {
      setMetricsLoading(true);
      try {
        await loadMetricsData();
      } finally {
        setMetricsLoading(false);
      }
    };
    loadData();
  }, [loadMetricsData, shouldFetchData]);

  // Fetch timeseries data when aggregation or dependencies change
  useEffect(() => {
    if (!shouldFetchData) return;
    const loadData = async () => {
      setTimeseriesLoading(true);
      try {
        await fetchTimeseriesData();
      } finally {
        setTimeseriesLoading(false);
      }
    };
    loadData();
  }, [fetchTimeseriesData, shouldFetchData]);

  const handleValueStreamChange = (value: string) => {
    setSelectedValueStreamId(value);
  };

  // Show configuration prompt if not configured
  if (!isConfigured || !apiSettings.selectedModelId) {
    return (
      <PageSection section={section}>
        <div className="flex flex-col items-center justify-center p-8 text-center h-full">
          <p className="text-muted-foreground mb-4">
            Please configure your settings to get started.
          </p>
          <Button onClick={() => setShowModelSelector(true)}>
            Configure Settings
          </Button>
        </div>
      </PageSection>
    );
  }

  // Don't try to render with the hook result until we're sure we should fetch data
  if (!shouldFetchData) {
    return (
      <PageSection section={section}>
        <div className="flex flex-col items-center justify-center p-8 text-center h-full">
          <p className="text-muted-foreground mb-4">
            Loading configuration...
          </p>
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PageSection>
    );
  }

  return (
    <PageSection section={section}>
      <div className="flex flex-col h-full max-h-full overflow-hidden">
        <div className="flex-shrink-0 pb-4 border-b">
          <Select
            value={selectedValueStreamId || undefined}
            onValueChange={handleValueStreamChange}
          >
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Value streams loading..." />
            </SelectTrigger>
            <SelectContent>
              {valueStreams.map((vs) => (
                <SelectItem key={vs.id} value={vs.id}>
                  {vs.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-grow min-h-0 overflow-auto relative">
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
              <Alert variant="destructive" className="max-w-md">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="flex space-x-4 mt-4">
                <Button
                  variant="outline"
                  onClick={fetchValueStreams}
                >
                  Retry
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowModelSelector(true)}
                  title="Open Settings"
                >
                  <Settings2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

            <MetricsLibrary 
              settings={{
                selectedCapability: settings.selectedCapability || 'all'
              }} 
              metrics={metrics}
              timeseriesData={timeseriesData}
              loading={timeseriesLoading || metricsLoading || valueStreamsLoading || graphDataLoading}
              timeseriesError={timeseriesError}
              aggregation={aggregation}
              setAggregation={setAggregation}
              showKPIMode={showKPIMode}
              setShowKPIMode={setShowKPIMode}
              graphData={graphData}
              timeRange={timeRange}
              setTimeRange={setTimeRange}
            />
          {/* )} */}
        </div>
      </div>
    </PageSection>
  );
} 